import { PackageConfig, PriceValue } from '@ca/common/types'
import useOrderValues from '@order/hooks/values/useOrderValues'

import { VariableValues } from '@order/utils/OrderVariables'
import {
  getPackageVariant,
  getUpgrades,
} from '@order/utils/packages/PackagesUtils'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession from 'hooks/useSession'
import { useCallback, useMemo } from 'react'
import useBundleType, { BundleParams } from './useBundleType'
import { useDeal } from './useDeal'
import { useLocalizedDeals } from './useDeals'

type PackageParams = {
  bundle?: BundleParams
}

export const usePackages = (params?: PackageParams) => {
  const config = useRemoteConfig()

  if (!config) {
    throw new Error('Remote config not available')
  }

  const { dealKey, dealUpgradeKey } = useBundleType(params?.bundle)

  const dealsState = useSession((state) => state.deals)
  const dealId = dealsState[dealKey]?.dealId
  const dealUpgradeId = dealsState[dealUpgradeKey]?.dealId

  const baseDealPackageId = dealsState[dealKey]?.basePackageId
  const baseActivePackageId = dealsState[dealKey]?.activePackageId
  const baseUpgradePackageId = dealsState[dealUpgradeKey]?.basePackageId
  const upgradeActivePackageId = dealsState[dealUpgradeKey]?.activePackageId
  const initialPackageId = baseUpgradePackageId || baseDealPackageId
  const activePackageId = upgradeActivePackageId || baseActivePackageId
  const dealUpgrade = useDeal(dealUpgradeId)
  const baseDeal = useDeal(dealId)
  const selectedDeal = dealUpgrade || baseDeal

  const packages = config.packages
  const initialPackage = initialPackageId ? packages[initialPackageId] : null
  const activePackage = activePackageId ? packages[activePackageId] : null

  const basePackage = baseDealPackageId ? packages[baseDealPackageId] : null

  return {
    packages,
    initialPackage,
    activePackage,
    initialPackageId,
    baseDeal,
    dealUpgrade,
    selectedDeal,
    basePackage,
  }
}
export const usePackageVariant = (item: PackageConfig | null | undefined) => {
  const config = useRemoteConfig()
  if (!config) {
    throw new Error('Config is required')
  }
  const { values, refraction } = useOrderValues({})

  return useMemo(() => {
    return getPackageVariant(item, config, { values, refraction })
  }, [item, config, values, refraction])
}

export const usePackageUpgrades = () => {
  const {
    packages,
    baseDeal,
    selectedDeal,
    initialPackage,

    basePackage,
    dealUpgrade,
    activePackage,
  } = usePackages()
  const { bundleType } = useBundleType()
  const deals = useLocalizedDeals()
  const values = useOrderValues({
    states: {},
    bundleType,
  })
  const isTypeUpgrade = basePackage?.type !== initialPackage?.type
  const fromDeal =
    deals && isTypeUpgrade && baseDeal?.vfDeal
      ? deals[baseDeal?.vfDeal]
      : baseDeal

  const upgrades = getUpgrades({
    deal: fromDeal,
    upgradeDeals:
      deals &&
      fromDeal?.upgradeDeals?.map((id) => deals[id]).filter((deal) => !!deal),
    packages,
    basePackage: activePackage || initialPackage,
    values,
  })

  return {
    baseDeal,
    packages,
    upgrades,
    initialPackage,
    basePackage,
    selectedDeal,
    dealUpgrade,
    activePackage,
  }
}

export const useCurrentPackageType = (params?: PackageParams) => {
  const { initialPackage, activePackage } = usePackages(params)

  const type = activePackage?.type ?? initialPackage?.type
  return type ?? 'sv'
}

export const useNextPackageVariant = () => {
  const packages = usePackages()

  const current = packages.activePackage ?? packages.initialPackage
  const config = useRemoteConfig()
  const { values, refraction } = useOrderValues({})

  const getNextPackageVariant = useCallback(
    (newValues: VariableValues) => {
      if (!config || !current) {
        return null
      }
      const currentVariant =
        getPackageVariant(current, config, {
          values,
          refraction,
        }) ?? current

      if (!currentVariant) {
        return null
      }

      const nextVariant = getPackageVariant(current, config, {
        values: {
          ...values,
          ...newValues,
        },
        refraction,
      })
      if (!nextVariant) {
        return null
      }
      if (nextVariant.id === currentVariant?.id) {
        return null
      }
      const newPrice = PriceValue.safe(nextVariant.price, config?.region)
      const basePrice = PriceValue.safe(currentVariant.price, config?.region)
      const priceDiff = Math.max(newPrice - basePrice, 0)
      if (priceDiff === 0) {
        return null
      }

      return {
        current: currentVariant,
        next: nextVariant,
        package: current,
      }
    },
    [current, config, values, refraction]
  )

  return getNextPackageVariant
}
